import { useMutation, useQueryClient } from '@tanstack/react-query';

import { TarActionItemCode } from '@ekhodealer/ekho-common';

import type { ShipSellerFormsToEkhoBody,ShipSellerFormsToEkhoResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to ship seller forms to Ekho
 */
export const shipSellerFormsToEkho = async (
	servicesProviderId: string,
	orderId: string,
	body: ShipSellerFormsToEkhoBody,
): Promise<ShipSellerFormsToEkhoResponse> => {
	const response = await apiClient.post<ShipSellerFormsToEkhoResponse>(
		ordersEndpoints.shipSellerFormsToEkho(servicesProviderId, orderId),
		body,
	);
	return response.data;
};

/**
 * Hook for shipping seller forms to Ekho
 * Uploads form scans and generates a UPS return shipping label for the dealer return packet
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useShipSellerFormsToEkho = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (body: ShipSellerFormsToEkhoBody) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to ship seller forms to Ekho without an order ID or services provider ID.',
				);
			}

			return shipSellerFormsToEkho(servicesProviderId, orderId, body);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, TarActionItemCode.SHIP_SELLER_FORMS_TO_EKHO);
		},
	});
};
